import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress, Grid, MenuItem, Typography } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import { FHAutocomplete, FButtonDropdown, FHConfirmDialog, FSubmenu, FHChip } from '../../../base';
import {
  providerActions,
  clientActions,
  treatmentPlanActions,
  treatmentRecommendationActions,
  medicalHistoryActions,
  dischargeTransitionActions,
  assessmentActions,
  abaTherapyServicesActions,
  educationBackgroundActions,
  clientFamilyBackgroundActions,
  providerAssignmentActions,
  sessionActions,
  goalActions,
} from '../../../../store';
import { history, areUserRolesIn, formatEntityId } from '../../../../helpers';
import {
  Themes,
  Roles,
  ProgressBarThemes,
  ENTITY_PREFIX,
  PayorResponsibilityEnum,
  BAYS,
  TREATMENT_PLAN_BASE_PATH,
} from '../../../../constants';
import { TreatmentPlanStatus } from '../constants/general';
import { ClinicalStatus } from '../../../../admin/constants/general';
import ActiveIcon from '../../../svgs/ActiveIcon';
import StatusIcon from '../../../svgs/TPStatusIcon';
import CompletedStatusIcon from '../../../svgs/CompletedStatusIcon';
import EditStatusIcon from '../../../svgs/EditStatusIcon';
import InProgressStatusIcon from '../../../svgs/InProgressStatusIcon';
import ReadonlyStatusIcon from '../../../svgs/ReadonlyStatusIcon';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_ORANGE,
  FORTA_SUCCESS,
  FORTA_WHITE,
} from '../../../../utils/colors';
import { getTreatmentPlanStatus } from '../utils';
import PdfDocument from '../PdfDocument';
import {
  clientOptionFromPlan,
  getStatusBarBayChipLabel,
  getStatusBarBtProviderDisplayNames,
  getTpRouteSegment,
} from './utils/general';
import { useCreateTreatmentPlan } from './hooks';

import type { ChangeEvent, HTMLAttributes } from 'react';
import type { RootState } from '../../../../store';
import type {
  IClient,
  IOption,
  ITreatmentPlan,
  ITrialSummary,
  Bays,
} from '../../../../utils/types';

function StatusBar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const { list: clients = [], loading: clientLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const { allClientsList: allClients = [] } = useSelector((state: RootState) => state.clients);
  const {
    current: treatmentPlan,
    list: treatmentPlans,
    loading: tpLoading,
    currentTpLoading,
    activeTreatmentPlan,
    tpStatusIdByLabel,
    tpPriorAuthorizations,
    tpPriorAuthorizationsLoading,
  } = useSelector((state: RootState) => state.treatmentPlans);
  const { recommendation, treatmentPlanId: recommendationTpId } = useSelector(
    (state: RootState) => state.treatmentRecommendations,
  );
  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { multiTrialSummary, loading: sessionLoading } = useSelector(
    (state: RootState) => state.sessions,
  );
  const { tpReportGoalsList: selectedTPGoals, loading: goalLoading } = useSelector(
    (state: RootState) => state.goals,
  );

  const componentRef = useRef(null);
  const createTreatmentPlan = useCreateTreatmentPlan();

  const { provider, coAssignedProviders } = useSelector((state: RootState) => state.providers);
  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const [tpClients, setTpClients] = useState<IClient[]>([]);
  const [dob, setDob] = useState<string>('');
  const [showConfirmationToSwitchClient, setShowConfirmationToSwitchClient] =
    useState<boolean>(false);
  const [showConfirmationToCreatePlan, setShowConfirmationToCreatePlan] = useState<boolean>(false);
  const [tpBay, setTPBay] = useState<Bays | null>(null);
  const [tpIdToCloneFrom, setTpIdToCloneFrom] = useState<number | null>(null);
  const [nextClientToSwitchTo, setNextClientToSwitchTo] = useState<IOption | null>(null);
  const [showExistingActiveTPAlert, setShowExistingActiveTPAlert] = useState<boolean>(false);
  const [showConfirmActivateTP, setShowConfirmActivateTP] = useState<boolean>(false);
  const [showActivationMessage, setShowActivationMessage] = useState<boolean>(false);
  const [showConfirmDeactivateTP, setShowConfirmDeactivateTP] = useState<boolean>(false);
  const [showConfirmEditTP, setShowConfirmEditTP] = useState<boolean>(false);
  const [showTPEditSuccess, setShowTPEditSuccess] = useState<boolean>(false);
  const [noActiveTPMessage, setNoActiveTPMessage] = useState<string>('');
  const [openConfirmDownload, setOpenConfirmDownload] = useState<boolean>(false);
  const [showTpActions, setShowTpActions] = useState<boolean>(false);
  const [hasNoConcludedTP, setHasNoConcludedTP] = useState<boolean>(false);
  const [switchingTpId, setSwitchingTpId] = useState<number | null>(null);
  const [showConfirmSwitchTP, setShowConfirmSwitchTP] = useState<boolean>(false);
  const [clientGoalIds, setClientGoalIds] = useState<number[]>([]);
  const [reportDownloading, setReportDownloading] = useState<boolean>(false);
  const [openReportConfirmDialog, setOpenReportConfirmDialog] = useState<boolean>(false);
  const [isReportGenerating, setIsReportGenerating] = useState<boolean>(false);
  const [showConfirmDeleteTP, setShowConfirmDeleteTP] = useState<boolean>(false);

  const inProgressTpId = treatmentPlans?.find(
    (tp) => tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Started],
  )?.id;

  const selectedClientBTProviders = useMemo(
    () =>
      getStatusBarBtProviderDisplayNames({
        userRoles: user?.roles,
        coAssignedProviders,
        providerAssignments,
      }),
    [providerAssignments, coAssignedProviders, user?.roles],
  );

  const handleResetRunValidationOnLoad = useCallback(() => {
    dispatch(
      medicalHistoryActions.updateState({
        key: 'triggerValidationsOnPageLoad',
        data: false,
      }),
    );
    dispatch(
      dischargeTransitionActions.updateState({
        key: 'triggerValidationsOnPageLoad',
        data: false,
      }),
    );
    dispatch(
      assessmentActions.updateState({
        key: 'triggerValidationsOnPageLoad',
        data: false,
      }),
    );
    dispatch(
      abaTherapyServicesActions.updateState({
        key: 'triggerValidationsOnPageLoad',
        data: false,
      }),
    );
    dispatch(
      educationBackgroundActions.updateState({
        key: 'triggerValidationsOnPageLoad',
        data: false,
      }),
    );
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'triggerValidationsOnPageLoad',
        data: false,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(providerActions.getProvider({ getAccessTokenSilently }));
    dispatch(treatmentPlanActions.getTreatmentPlanStatuses({ getAccessTokenSilently }));
    return () => {
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      dispatch(treatmentPlanActions.clearTreatmentPlanState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.roles) return;
    if (areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager])) {
      dispatch(
        clientActions.getList({
          getAccessTokenSilently,
          clinical_status: [
            ClinicalStatus.CLINICAL,
            ClinicalStatus.OFFBOARDED,
            ClinicalStatus.ON_HOLD,
          ],
        }),
      );
    } else {
      dispatch(clientActions.getList({ getAccessTokenSilently }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.roles]);

  useEffect(() => {
    if (!user?.roles) return;
    if (areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager])) {
      setTpClients(allClients);
    } else {
      setTpClients(clients);
    }
  }, [user?.roles, clients, allClients]);

  useEffect(() => {
    if (!selectedClient) {
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      setDob('');
      return;
    }
    dispatch(
      treatmentPlanActions.getTPPriorAuthorizations({
        getAccessTokenSilently,
        pa_expires_within_days: 180,
        client_id: selectedClient?.id,
      }),
    );

    if (areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor])) {
      dispatch(
        providerActions.getCoAssignedProviders({
          clinical_relationship: ['prbt', 'vbt'],
          client_id: selectedClient?.id,
          getAccessTokenSilently,
        }),
      );
    } else if (!areUserRolesIn(user?.roles, [Roles.Prbt])) {
      dispatch(
        providerAssignmentActions.getList({
          getAccessTokenSilently,
          active_only: true,
          client_id: selectedClient?.id,
        }),
      );
    }

    const client =
      tpClients?.find((item: IClient) => item.id === selectedClient.id) ?? treatmentPlan?.client;
    async function fetchAndSetTpAndClientDetails(selectedClient: IOption | null) {
      if (!selectedClient) return;
      await dispatch(
        treatmentPlanActions.getTreatmentPlanByQuery({
          query: { clientId: selectedClient.id, with_activation_logs: true },
          getAccessTokenSilently,
          skipCurrentTpChange: treatmentPlan?.client_id === selectedClient?.id,
          activeRoute: treatmentPlan ? location.pathname.split('/')[3] : '',
          fromTP: true,
        }),
      );
      dispatch(clientActions.setSelected(client));
      dispatch(
        clientActions.getClientSpecificPayorsMapping({
          getAccessTokenSilently,
          clientId: selectedClient.id,
        }),
      );
      handleResetRunValidationOnLoad();
    }
    fetchAndSetTpAndClientDetails(selectedClient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  useEffect(() => {
    if (!tpStatusIdByLabel[TreatmentPlanStatus.Completed] || !treatmentPlans.length) {
      setHasNoConcludedTP(true);
      setShowTpActions(false);
      return;
    }

    const tpsNotOpen = treatmentPlans.filter((plan: ITreatmentPlan) => {
      if (
        plan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed] ||
        plan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing] ||
        plan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly]
      ) {
        return true;
      }
      return false;
    });
    const concludedTPs = treatmentPlans.filter(
      (plan: ITreatmentPlan) => plan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed],
    );
    setHasNoConcludedTP(concludedTPs.length === 0);
    setShowTpActions(tpsNotOpen?.length > 0);
  }, [treatmentPlans, tpStatusIdByLabel]);

  useEffect(() => {
    // Respond to treatmentPlanId change in url and fetch data if needed
    if (!treatmentPlanId) return;
    if (isNaN(parseInt(treatmentPlanId))) {
      if (!treatmentPlans?.length && selectedClient) {
        toast.error('Please create a treatment plan');
      } else if (selectedClient) {
        toast.error(`Treatment plan with id ${treatmentPlanId} not found`);
      } else {
        toast.error('Please select a client');
      }
      history.navigate(TREATMENT_PLAN_BASE_PATH);
      return;
    }

    if (!treatmentPlan || treatmentPlan?.id !== parseInt(treatmentPlanId)) {
      dispatch(
        treatmentPlanActions.getTreatmentPlanById({
          treatmentPlanId,
          getAccessTokenSilently,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlanId]);

  useEffect(() => {
    if (treatmentPlans.length > 0 && !treatmentPlan) {
      setSelectedClient(null);
      return;
    }

    // If url is updated with new tp id and the selected client id is not same as new TP's client id,
    // Then update selectedClient with new tp's client id
    if (!selectedClient || selectedClient?.id !== treatmentPlan?.client_id) {
      const clientOption = clientOptionFromPlan(treatmentPlan, tpClients);
      if (clientOption) {
        setSelectedClient(clientOption);
      }
    }

    if (treatmentPlan?.client?.dob) {
      setDob(moment(treatmentPlan?.client?.dob).format('MM/DD/YYYY'));
    } else {
      setDob('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan, tpClients]);

  useEffect(() => {
    if (!treatmentPlans?.length || !treatmentPlan || !tpStatusIdByLabel) return;

    const concludedTPs = treatmentPlans?.filter(
      (tp: ITreatmentPlan) => tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed],
    );

    if (treatmentPlan.id !== recommendationTpId) {
      dispatch(
        treatmentRecommendationActions.getRecommendation({
          treatmentPlanId: treatmentPlan.id,
          getAccessTokenSilently,
          forceUpdate: true,
        }),
      );
    }

    // if no active tp and has atleast 1 concluded TP show alert
    if (
      !activeTreatmentPlan &&
      !tpLoading &&
      concludedTPs.length > 0 &&
      Boolean(selectedClient?.id)
    ) {
      if (
        treatmentPlan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Started] ||
        treatmentPlan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing]
      ) {
        setNoActiveTPMessage(
          'The Client has no active Treatment Plan. Please activate one of the concluded plans.',
        );
      }
      if (treatmentPlan.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed]) {
        setNoActiveTPMessage(
          `The Client has no active Treatment Plan. The BT will not be able to create session notes.`,
        );
      }
    } else {
      setNoActiveTPMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlans, treatmentPlan, activeTreatmentPlan, tpStatusIdByLabel]);

  useEffect(() => {
    if (!selectedClient) {
      setClientGoalIds([]);
      dispatch(
        goalActions.updateState({
          key: 'list',
          value: [],
        }),
      );
      dispatch(sessionActions.updateState({ key: 'multiTrialSummary', data: [] }));
      return;
    }
  }, [selectedClient, dispatch, getAccessTokenSilently]);

  useEffect(() => {
    if (!treatmentPlan) {
      setClientGoalIds([]);
      dispatch(
        goalActions.updateState({
          key: 'list',
          value: [],
        }),
      );
      dispatch(sessionActions.updateState({ key: 'multiTrialSummary', data: [] }));
      return;
    }
    const fetchData = async () => {
      await dispatch(
        goalActions.getGoals({
          getAccessTokenSilently,
          treatmentPlanId: treatmentPlan?.id,
          tp_report_goals: true,
        }),
      );
      await dispatch(
        treatmentRecommendationActions.getRecommendation({
          treatmentPlanId: treatmentPlan?.id,
          getAccessTokenSilently,
        }),
      );
    };
    fetchData();
  }, [treatmentPlan, dispatch, getAccessTokenSilently]);

  useEffect(() => {
    if (!selectedTPGoals.length) {
      setClientGoalIds([]);
      dispatch(sessionActions.updateState({ key: 'multiTrialSummary', data: [] }));
      return;
    }
    const goalIds = selectedTPGoals
      .map((goal) => goal.id)
      .filter((id): id is number => id !== undefined);
    setClientGoalIds(goalIds);
  }, [selectedTPGoals, dispatch]);

  const isMultiTrialSummaryEmpty = (summary: ITrialSummary[]): boolean => {
    return (
      summary.length === 0 || summary.every((item) => Array.isArray(item) && item.length === 0)
    );
  };

  const generatePDFReport = async () => {
    if (!selectedClient || !treatmentPlan) return;
    if (!clientGoalIds.length) {
      toast.error('No goals found for the selected treatment plan');
      return;
    }
    const params = {
      client_id: selectedClient.id,
      start_date: moment().subtract(365, 'days').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
      goal_ids: clientGoalIds,
    };

    try {
      const res = await dispatch(
        sessionActions.getMultiTrialSummary({ params, getAccessTokenSilently }),
      );
      if (res.error) {
        toast.error('Failed to generate report, please try again later');
        return;
      }
    } catch {
      toast.error('An unexpected error occurred while generating the report');
    } finally {
      setIsReportGenerating(false);
      setReportDownloading(false);
    }
  };

  useEffect(() => {
    if (!selectedClient || !treatmentPlan || !clientGoalIds.length || !isReportGenerating) return;
    if (multiTrialSummary && !isMultiTrialSummaryEmpty(multiTrialSummary)) {
      setOpenReportConfirmDialog(true);
      setIsReportGenerating(false);
    } else if (multiTrialSummary) {
      toast.error('There are no trials available for the selected treatment plan.');
      setIsReportGenerating(false);
      return;
    } else {
      toast.error('There are no trials available for the selected treatment plan.');
      setIsReportGenerating(false);
      return;
    }
  }, [multiTrialSummary]);

  const handlePDFReportDownload = async () => {
    setReportDownloading(true);
    setIsReportGenerating(true);
    await generatePDFReport();
  };

  const handlePrint = useReactToPrint({
    documentTitle: `${selectedClient?.label}_${formatEntityId(treatmentPlan?.id ?? '', 'FT')}`,
    contentRef: componentRef,
  });

  const tpEndingDays = useMemo(() => {
    if (!tpPriorAuthorizations.length || !treatmentPlan) return null;
    let tpPriorAuthorization = tpPriorAuthorizations.find(
      (pa) =>
        pa.active_treatment_plan_id === treatmentPlan?.id &&
        pa.payor_responsibility === PayorResponsibilityEnum.PRIMARY,
    );
    if (!tpPriorAuthorization) {
      tpPriorAuthorization = tpPriorAuthorizations.find(
        (pa) =>
          pa.active_treatment_plan_id === treatmentPlan?.id &&
          pa.payor_responsibility === PayorResponsibilityEnum.SECONDARY,
      );
    }
    if (!tpPriorAuthorization) return null;
    return tpPriorAuthorization?.days_until_expiry;
  }, [tpPriorAuthorizations, treatmentPlan]);

  const status = useMemo(() => {
    return getTreatmentPlanStatus(treatmentPlan, tpStatusIdByLabel);
  }, [treatmentPlan, tpStatusIdByLabel]);

  const handleNoActiveTpAlertClose = () => {
    setNoActiveTPMessage('');
  };

  const handleChangeTP = (id: number | null) => {
    if (!id || !location.pathname) return;

    const route = getTpRouteSegment(location.pathname);
    history.navigate(`${TREATMENT_PLAN_BASE_PATH}/${id}/${route}`);
    setShowConfirmSwitchTP(false);
    setSwitchingTpId(null);
  };

  const confirmActivateTP = async () => {
    if (showActivationMessage) {
      setShowConfirmActivateTP(false);
      setShowActivationMessage(false);
      return;
    }
    if (!treatmentPlan) return;
    const response = await dispatch(
      treatmentPlanActions.activateTreatmentPlan({
        getAccessTokenSilently,
        treatmentPlanId: treatmentPlan.id,
      }),
    );
    if (!response.error) {
      setShowActivationMessage(true);
    }
  };

  const confirmDeactivateTP = async () => {
    if (!treatmentPlan) return;
    const response = await dispatch(
      treatmentPlanActions.deactivateTreatmentPlan({
        getAccessTokenSilently,
        treatmentPlanId: treatmentPlan.id,
      }),
    );
    if (!response.error) {
      setShowConfirmDeactivateTP(false);
    }
  };

  const confirmEditTP = async () => {
    const response = await dispatch(
      treatmentPlanActions.editTreatmentPlan({
        getAccessTokenSilently,
        treatmentPlanId: treatmentPlan?.id,
      }),
    );
    if (!response.error) {
      setShowConfirmEditTP(false);
      setShowTPEditSuccess(true);
    }
  };

  const handleDownloadTreatmentPlan = async () => {
    if (!treatmentPlan) return;
    setOpenConfirmDownload(false);
    const response = await dispatch(
      treatmentPlanActions.downloadTreatmentPlan({
        getAccessTokenSilently,
        treatmentPlanId: treatmentPlan.id,
      }),
    );
    if (response.payload?.document_url) {
      window.location.href = response.payload?.document_url;
    } else if (response.error?.message) {
      toast.error(response.error?.message);
    } else {
      toast.error('Something went wrong while downloading the pdf');
    }
  };

  const handleActivateTP = () => {
    if (activeTreatmentPlan?.id !== undefined) {
      setShowExistingActiveTPAlert(true);
      return;
    } else {
      setShowConfirmActivateTP(true);
    }
  };

  const handleCreateOrCloneTP = (bay: Bays) => {
    if (!treatmentPlan || !treatmentPlan.id) {
      handleCreatePlan(bay, null);
    } else {
      setShowConfirmationToCreatePlan(true);
      setTPBay(bay);
      setTpIdToCloneFrom(treatmentPlan.id ?? null);
    }
  };

  const handleCreatePlan = async (bay: Bays, tpIdToCloneFrom: number | null) => {
    if (!selectedClient) return;
    const created = await createTreatmentPlan(Number(selectedClient.id), bay, tpIdToCloneFrom);
    if (!created) return;

    setTPBay(null);
    setTpIdToCloneFrom(null);
    toast.success('Treatment Plan created');
  };

  const handleDeleteTP = async () => {
    if (!treatmentPlan) return;
    setShowConfirmDeleteTP(false);
    const response = await dispatch(
      treatmentPlanActions.deleteTreatmentPlan({
        getAccessTokenSilently,
        treatmentPlanId: treatmentPlan.id,
      }),
    );
    if (!response.error) {
      history.navigate('/dashboard');
    }
  };

  const handleViewEditActivationClick = () => {
    history.navigate(`/tp-activation`, { state: { client: selectedClient } });
  };

  const TpStatus = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {treatmentPlan?.is_active ? (
          <ActiveIcon color={status.color} />
        ) : (
          <StatusIcon color={status.color} />
        )}
        <Typography fontWeight={treatmentPlan?.is_active ? 'bold' : 'normal'} ml={1}>
          {treatmentPlan?.is_active ? 'Active' : 'Inactive'}
        </Typography>
        <Typography mx={0.5}>-</Typography>
        <Typography>{status.text}</Typography>
      </Box>
      {status.signatureNeeded && (
        <Box>
          <Typography textAlign="left" fontSize={14} color={FORTA_ERROR}>
            (Signature needed)
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Box
      data-testid="treatmentPlanBar"
      sx={{
        mt: 2,
        p: 2,
        height: 'auto',
        borderRadius: 2,
        backgroundColor: FORTA_BLUE.light3,
        border: `1px solid ${FORTA_BLUE.light2}`,
      }}
    >
      <Grid container spacing={2}>
        <Grid item md={2.5}>
          <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
            Client name
          </Typography>
          <FHAutocomplete
            data-testid="clientName"
            fullWidth
            placeholder="Select client"
            colorTheme={Themes.Primary}
            inputHeight={40}
            disabled={
              clientLoading ||
              tpLoading ||
              currentTpLoading ||
              providerAssignmentLoading ||
              sessionLoading ||
              goalLoading
            }
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            options={tpClients?.map((client: IClient) => ({
              id: client.id,
              label: `${client.first_name} ${client.last_name}`,
            }))}
            renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
              return (
                <li {...props} key={option.id} data-testid={`clientName${option.id}`}>
                  {option.label}
                </li>
              );
            }}
            value={selectedClient}
            onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              const prevId = selectedClient?.id ?? null;
              const nextId = newValue?.id ?? null;
              if (prevId !== null && nextId !== null && prevId === nextId) {
                return;
              }
              if (
                areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor]) &&
                Boolean(selectedClient)
              ) {
                setNextClientToSwitchTo(newValue);
                setShowConfirmationToSwitchClient(true);
              } else {
                setSelectedClient(newValue);
              }
            }}
          />
        </Grid>
        <Grid item md={1.5}>
          <Typography fontWeight={'bold'}>Client DOB</Typography>
          <Typography data-testid="dobBox">{dob}</Typography>
        </Grid>
        <Grid item md={2} textAlign={'left'}>
          {treatmentPlan?.bay !== BAYS.BAY_6 && (
            <>
              <Typography fontWeight={'bold'}>BT name</Typography>
              <Box display={'flex'} flexWrap={'wrap'} maxWidth={400} alignContent={'center'}>
                {selectedClient &&
                  selectedClientBTProviders?.map((name: string, i: number) => (
                    <Typography data-testid={`${name.replace(/\s+/g, '_')}`} key={i}>{`${name}${
                      i === selectedClientBTProviders?.length - 1 ? '' : ';'
                    }`}</Typography>
                  ))}
              </Box>
            </>
          )}
        </Grid>
        <Grid item md={2} textAlign={'left'} data-testid="treatmentPlanStatusSection">
          <Typography fontWeight={'bold'}>Status</Typography>
          <Box sx={{ height: 40 }} data-testid="treatmentPlanStatusText">
            {treatmentPlan && <TpStatus />}
          </Box>
        </Grid>

        {/* TP active status and bay value display */}
        <Grid item md={3}>
          <Box display={'flex'} alignItems={'center'} flexDirection={'column'}>
            {treatmentPlan?.bay && (
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <Typography
                  fontSize={12}
                  fontWeight={'bold'}
                  mt={0.5}
                  mr={tpEndingDays !== null ? 5 : 1}
                >
                  Bay:
                </Typography>
                <Typography fontWeight={700} fontSize={12} color={FORTA_WHITE}></Typography>

                <FHChip
                  sx={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    width: 'fit-content',
                    mb: 1,
                    px: 1.5,
                    py: 0.5,
                    mr: 1,
                  }}
                  bgColor={treatmentPlan?.bay === BAYS.BAY_2_5 ? FORTA_ORANGE : FORTA_BLUE.primary}
                  content={getStatusBarBayChipLabel(treatmentPlan?.bay)}
                />
              </Box>
            )}
            {!currentTpLoading && !tpPriorAuthorizationsLoading ? (
              selectedClient && tpEndingDays !== null ? (
                <Box sx={{ display: 'flex' }}>
                  <Typography fontSize={12} fontWeight={'bold'} mt={0.5} mr={1}>
                    Expiring in:{' '}
                  </Typography>
                  <FHChip
                    sx={{
                      fontSize: 12,
                      fontWeight: 'bold',
                      width: '85px',
                      mb: 1,
                      px: 1.5,
                      py: 0.5,
                      mr: 1,
                    }}
                    bgColor={
                      tpEndingDays <= 30
                        ? FORTA_ERROR
                        : tpEndingDays <= 60
                          ? FORTA_ORANGE
                          : FORTA_SUCCESS
                    }
                    content={
                      tpEndingDays <= 0
                        ? 'Expired'
                        : `${tpEndingDays} ${tpEndingDays === 1 ? 'day' : 'days'}`
                    }
                  />
                </Box>
              ) : null
            ) : (
              <CircularProgress sx={{ color: FORTA_BLUE.primary }} size="30px" />
            )}
          </Box>
        </Grid>

        <Grid data-testid="tpOperationDropDown" item md={1}>
          <FButtonDropdown
            dataTestId="treatmentPlanActionsDropDown"
            coloredBackground
            colorTheme={Themes.Primary}
            disabled={!selectedClient}
          >
            {treatmentPlans?.length > 1 && (
              <FSubmenu label="Select Plan" colorTheme={Themes.Primary}>
                {(treatmentPlans || []).map((tp: ITreatmentPlan) => {
                  return (
                    <MenuItem
                      data-testid="selectPlan"
                      key={tp.id}
                      disabled={treatmentPlan?.id === tp.id}
                      onClick={() => {
                        if (tp.id === undefined) return;
                        setShowConfirmSwitchTP(true);
                        setSwitchingTpId(tp.id);
                      }}
                      sx={{ padding: 0 }}
                    >
                      <Box sx={{ width: 24, mb: -0.5 }}>
                        {activeTreatmentPlan?.id === tp.id && <ActiveIcon />}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>{tp.id ? formatEntityId(tp.id, 'FT') : ''}</Box>
                      <Box sx={{ ml: 1 }}>
                        {tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Started] && (
                          <InProgressStatusIcon />
                        )}
                        {tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing] && (
                          <EditStatusIcon />
                        )}
                        {tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed] && (
                          <CompletedStatusIcon />
                        )}
                        {tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly] && (
                          <ReadonlyStatusIcon />
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </FSubmenu>
            )}
            <FSubmenu
              dataTestId="createTp"
              label={treatmentPlans?.length > 0 ? 'Clone plan' : 'Create plan'}
              colorTheme={Themes.Primary}
              disabled={
                Boolean(!selectedClient) ||
                !areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor]) ||
                (treatmentPlans?.length
                  ? treatmentPlan?.status_id !== tpStatusIdByLabel[TreatmentPlanStatus.Completed]
                  : false)
              }
            >
              {inProgressTpId ? (
                <Box px={1} boxShadow={'none'}>
                  <Typography
                    display="inline"
                    fontWeight={500}
                  >{`Please conclude in-progress treatment plan `}</Typography>
                  <Typography
                    display="inline"
                    sx={{
                      color: FORTA_BLUE.primary,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setShowConfirmSwitchTP(true);
                      setSwitchingTpId(inProgressTpId);
                    }}
                  >{`${formatEntityId(inProgressTpId, ENTITY_PREFIX.TreatmentPlan)}`}</Typography>
                  <Typography display="inline" fontWeight={500}>{`  before cloning.`}</Typography>
                </Box>
              ) : (
                <>
                  <MenuItem
                    data-testid="bay3_0TPCreate"
                    onClick={() => {
                      handleCreateOrCloneTP(BAYS.BAY_3);
                    }}
                    sx={{ padding: 0 }}
                  >
                    Bay 3.0 Full TP
                  </MenuItem>
                  <MenuItem
                    data-testid="bay2_6TPCreate"
                    onClick={() => {
                      handleCreateOrCloneTP(BAYS.BAY_2_6);
                    }}
                    sx={{ padding: 0 }}
                  >
                    Bay 2.6/7 Full TP
                  </MenuItem>
                  <MenuItem
                    data-testid="bay2_5TPCreate"
                    onClick={() => {
                      handleCreateOrCloneTP(BAYS.BAY_2_5);
                    }}
                    sx={{ padding: 0 }}
                  >
                    Bay 2.5 / 2.7 TP
                  </MenuItem>
                  <MenuItem
                    data-testid="bay6_0TPCreate"
                    onClick={() => {
                      handleCreateOrCloneTP(BAYS.BAY_6);
                    }}
                    sx={{ padding: 0 }}
                  >
                    Bay 6.0 Full TP
                  </MenuItem>
                </>
              )}
            </FSubmenu>

            {showTpActions && [
              <MenuItem
                data-testid="editTp"
                onClick={() => setShowConfirmEditTP(true)}
                key={'edit'}
                disabled={
                  !areUserRolesIn(user?.roles, [Roles.Bcba]) ||
                  treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Started] ||
                  treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing] ||
                  treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly] ||
                  hasNoConcludedTP
                }
              >
                Edit
              </MenuItem>,
              <MenuItem
                data-testid="activateTp"
                onClick={handleActivateTP}
                key={'activate'}
                disabled={
                  !areUserRolesIn(user?.roles, [Roles.Bcba]) ||
                  treatmentPlan?.id === activeTreatmentPlan?.id ||
                  treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Started] ||
                  treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing] ||
                  treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly]
                }
              >
                Activate
              </MenuItem>,
              <MenuItem
                data-testid="deactivateTp"
                onClick={() => setShowConfirmDeactivateTP(true)}
                key={'deactivate'}
                disabled={
                  !areUserRolesIn(user?.roles, [Roles.Bcba]) ||
                  !(treatmentPlan?.id === activeTreatmentPlan?.id) ||
                  hasNoConcludedTP
                }
              >
                Deactivate
              </MenuItem>,
              <MenuItem
                data-testid="downloadTp"
                onClick={() => setOpenConfirmDownload(true)}
                //disable if treatment plan not completed or read only
                key={'download'}
                disabled={
                  !areUserRolesIn(user?.roles, [
                    Roles.Admin,
                    Roles.AssistantAssessor,
                    Roles.Bcba,
                    Roles.Biller,
                    Roles.InvoiceCoordinator,
                    Roles.ClientSuccessManager,
                    Roles.QaSpecialist,
                  ]) ||
                  !treatmentPlan?.signature ||
                  !(
                    treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed] ||
                    treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly]
                  )
                }
              >
                Download
              </MenuItem>,
            ]}
            {areUserRolesIn(user?.roles, [Roles.Admin, Roles.Bcba]) && [
              <MenuItem
                data-testid="deleteTp"
                onClick={() => setShowConfirmDeleteTP(true)}
                key={'deleteTp'}
                disabled={
                  treatmentPlan?.status_id !== tpStatusIdByLabel[TreatmentPlanStatus.Started] ||
                  user?.email !== provider?.email
                }
              >
                Delete TP
              </MenuItem>,
              <MenuItem
                data-testid="viewEditActivation"
                onClick={handleViewEditActivationClick}
                key={'viewEditActivation'}
                disabled={user?.email !== provider?.email}
              >
                View / Edit Activation
              </MenuItem>,
            ]}
            {areUserRolesIn(user?.roles, [Roles.Admin, Roles.Bcba, Roles.ClientSuccessManager]) && (
              <MenuItem
                data-testid="downloadReport"
                onClick={handlePDFReportDownload}
                //disable if treatment plan not completed
                key={'downloadReport'}
                disabled={
                  reportDownloading ||
                  !treatmentPlan?.signature ||
                  !(
                    treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed] ||
                    treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly] ||
                    treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing]
                  )
                }
              >
                Progress report
              </MenuItem>
            )}
            {areUserRolesIn(user?.roles, [Roles.Admin]) && (
              <MenuItem
                data-testid="regeneratePDF"
                onClick={() => {
                  dispatch(
                    treatmentPlanActions.regenerateTreatmentPlanPDF({
                      getAccessTokenSilently,
                      treatmentPlanId: treatmentPlan?.id,
                    }),
                  );
                }}
                key={'regeneratePDF'}
                //disable if treatment plan not completed or read only
                disabled={
                  !treatmentPlan?.signature ||
                  !(
                    treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed] ||
                    treatmentPlan?.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly]
                  )
                }
              >
                Regenerate PDF
              </MenuItem>
            )}
          </FButtonDropdown>
        </Grid>
      </Grid>

      {/* Hidden PDF document component for generating PDFs.The PDF document must be present in the DOM to enable printing.
       Using display: none prevents the graph from rendering, so we use visibility: hidden and position: absolute to hide the component from the user. */}
      <Box style={{ visibility: 'hidden', position: 'absolute', top: '-9999px', width: '100vw' }}>
        <PdfDocument
          ref={componentRef}
          client={selectedClient}
          data={multiTrialSummary}
          goals={selectedTPGoals}
          treatmentPlan={{
            id: treatmentPlan?.id,
            label: formatEntityId(treatmentPlan?.id ?? ''),
          }}
          recommendation={recommendation}
        />
      </Box>

      <FHConfirmDialog
        open={showConfirmationToSwitchClient}
        colorTheme={Themes.Primary}
        content={
          showConfirmationToSwitchClient
            ? nextClientToSwitchTo
              ? 'Are you sure you want to change to a different client?'
              : 'Are you sure you want to clear the selected client?'
            : ''
        }
        submittext={'CONTINUE'}
        canceltext={'BACK TO CURRENT PLAN'}
        onClose={() => setShowConfirmationToSwitchClient(false)}
        onSubmit={() => {
          if (!nextClientToSwitchTo) {
            history.navigate(TREATMENT_PLAN_BASE_PATH);
          }
          setSelectedClient(nextClientToSwitchTo);
          setShowConfirmationToSwitchClient(false);
          setNextClientToSwitchTo(null);
        }}
      />
      <FHConfirmDialog
        open={showExistingActiveTPAlert && activeTreatmentPlan?.id !== undefined}
        colorTheme={Themes.Primary}
        content={`Treatment plan ${formatEntityId(
          activeTreatmentPlan?.id ?? '',
          'FT',
        )} is currently active. Please deactivate before activating a new treatment plan.`}
        submittext={'SWITCH TO ACTIVE PLAN'}
        canceltext={'CANCEL'}
        submitBtnTestId="submitConfirmDialog"
        onClose={() => setShowExistingActiveTPAlert(false)}
        onSubmit={() => {
          handleChangeTP(activeTreatmentPlan?.id ?? null);
          setShowExistingActiveTPAlert(false);
        }}
      />
      <FHConfirmDialog
        open={showConfirmActivateTP && Boolean(treatmentPlan)}
        colorTheme={Themes.Primary}
        content={
          showActivationMessage
            ? `Treatment plan ${
                treatmentPlan ? formatEntityId(treatmentPlan.id ?? '', 'FT') : ''
              } is now active.`
            : `Are you sure you want to activate treatment plan ${
                treatmentPlan ? formatEntityId(treatmentPlan.id ?? '', 'FT') : ''
              }?`
        }
        submittext={showActivationMessage ? 'CONTINUE' : 'ACTIVATE'}
        canceltext={'CANCEL'}
        submitBtnTestId="submitConfirmDialog"
        hideCancelButton={showActivationMessage}
        onClose={() => {
          setShowConfirmActivateTP(false);
          setShowActivationMessage(false);
        }}
        onSubmit={confirmActivateTP}
      />
      <FHConfirmDialog
        open={Boolean(noActiveTPMessage)}
        colorTheme={Themes.Primary}
        content={noActiveTPMessage}
        submittext={'CLOSE'}
        submitBtnTestId="submitConfirmDialog"
        hideCancelButton
        onClose={handleNoActiveTpAlertClose}
        onSubmit={handleNoActiveTpAlertClose}
      />
      <FHConfirmDialog
        open={Boolean(reportDownloading)}
        colorTheme={Themes.Primary}
        content={
          'Your treatment plan report is being processed. Please wait for the download to complete.'
        }
        hideCancelButton
        hideSubmitButton
        hideCloseIcon
        showProgressBar
        progressBarTheme={ProgressBarThemes.Secondary}
      />
      <FHConfirmDialog
        open={showConfirmDeactivateTP && Boolean(treatmentPlan)}
        colorTheme={Themes.Primary}
        content={`Are you sure you want to deactivate treatment plan ${
          treatmentPlan ? formatEntityId(treatmentPlan.id ?? '', 'FT') : ''
        }?`}
        submittext={'DEACTIVATE'}
        canceltext={'CANCEL'}
        submitBtnTestId="submitConfirmDialog"
        cancelBtnTestId="cancelButton"
        onClose={() => {
          setShowConfirmDeactivateTP(false);
        }}
        onSubmit={confirmDeactivateTP}
      />
      <FHConfirmDialog
        open={showConfirmEditTP}
        colorTheme={Themes.Primary}
        content={`Are you sure you want to edit treatment plan ${
          treatmentPlan ? formatEntityId(treatmentPlan.id ?? '', 'FT') : ''
        }?`}
        submittext={'CONTINUE TO EDIT'}
        submitBtnTestId={'submitEditBtn'}
        canceltext={'CANCEL'}
        cancelBtnTestId={'cancelEditBtn'}
        onClose={() => {
          setShowConfirmEditTP(false);
        }}
        onSubmit={confirmEditTP}
      />
      <FHConfirmDialog
        open={showTPEditSuccess}
        colorTheme={Themes.Primary}
        content={`The treatment plan is now in edit mode. `}
        submittext={'CONTINUE'}
        submitBtnTestId={'continueBtn'}
        hideCancelButton={true}
        onClose={() => {
          setShowTPEditSuccess(false);
        }}
        onSubmit={() => {
          setShowTPEditSuccess(false);
        }}
      />
      <FHConfirmDialog
        open={openConfirmDownload}
        colorTheme={Themes.Primary}
        content={'Are you sure you want to download this treatment plan?'}
        submittext={'Download'}
        canceltext={'Back to treatment plan'}
        onClose={() => setOpenConfirmDownload(false)}
        onSubmit={() => {
          handleDownloadTreatmentPlan();
        }}
      />
      <FHConfirmDialog
        open={openReportConfirmDialog}
        colorTheme={Themes.Primary}
        content={
          sessionLoading
            ? 'Generating report. Please wait...'
            : 'Are you sure you want to download this treatment plan report?'
        }
        submittext={'Download'}
        submitLoading={sessionLoading}
        disableSubmitButton={sessionLoading}
        canceltext={'Back to treatment plan'}
        onClose={() => setOpenReportConfirmDialog(false)}
        onSubmit={() => {
          handlePrint();
          setOpenReportConfirmDialog(false);
        }}
      />
      <FHConfirmDialog
        open={showConfirmationToCreatePlan}
        colorTheme={Themes.Primary}
        contentRenderer={() => {
          return (
            <Box sx={{ pt: 4, px: 1, mb: 12 }}>
              {treatmentPlan?.is_active ? (
                <>
                  <Typography display="inline" fontSize={18}>
                    Are you sure you want to clone the current
                  </Typography>
                  <Typography display="inline" fontWeight={700} fontSize={18} color={FORTA_SUCCESS}>
                    {' active '}
                  </Typography>
                  <Typography display="inline" fontSize={18}>
                    treatment plan
                  </Typography>
                  <Typography display="inline" fontSize={18}>{` ${formatEntityId(
                    treatmentPlan?.id ?? 0,
                    ENTITY_PREFIX.TreatmentPlan,
                  )} `}</Typography>
                  <Typography display="inline" fontSize={18}>{`  for client ${
                    selectedClient?.label ?? ''
                  }?`}</Typography>
                </>
              ) : (
                <>
                  <Typography display="inline" fontSize={18}>
                    You&apos;re about to clone treatment plan
                  </Typography>
                  <Typography display="inline" fontSize={18}>{` ${formatEntityId(
                    treatmentPlan?.id ?? 0,
                    ENTITY_PREFIX.TreatmentPlan,
                  )} `}</Typography>
                  <Typography display="inline" fontSize={18}>{` for client ${
                    selectedClient?.label ?? ''
                  }, but this plan is `}</Typography>
                  <Typography display="inline" fontSize={18} fontWeight={700} color={FORTA_ERROR}>
                    not active.
                  </Typography>
                </>
              )}
            </Box>
          );
        }}
        submittext={'CLONE PLAN'}
        canceltext={'CANCEL'}
        submitBtnTestId="submitConfirmDialog"
        onClose={() => setShowConfirmationToCreatePlan(false)}
        onSubmit={() => {
          setShowConfirmationToCreatePlan(false);
          if (tpBay) {
            handleCreatePlan(tpBay, tpIdToCloneFrom);
          }
        }}
      />
      <FHConfirmDialog
        open={showConfirmSwitchTP}
        colorTheme={Themes.Primary}
        content={
          'Are you sure you want to switch Treatment Plan? Any unsaved information will be lost.'
        }
        submittext={'Switch plan'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmSwitchTP(false)}
        onSubmit={() => {
          handleChangeTP(switchingTpId);
        }}
      />
      <FHConfirmDialog
        open={showConfirmDeleteTP}
        colorTheme={Themes.Primary}
        title={'Delete treatment plan'}
        contentRenderer={() => (
          <Box display="flex" flexDirection="column" justifyContent="center" px={1} py={6}>
            <Typography textAlign="center" fontSize={20} fontWeight={700}>
              Are you sure you want to delete treatment plan{' '}
              {treatmentPlan ? formatEntityId(treatmentPlan.id ?? '', 'FT') : ''}?
            </Typography>
            <Typography textAlign="center" fontSize={21} fontWeight={700} mt={1} mb={1}>
              This action cannot be undone.
            </Typography>
          </Box>
        )}
        submittext={'DELETE'}
        canceltext={'CANCEL'}
        onClose={() => setShowConfirmDeleteTP(false)}
        onSubmit={handleDeleteTP}
      />
    </Box>
  );
}

export default StatusBar;
